import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Activity, Users, Clock, CheckCircle2, Star } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import { ReviewsSection } from "../components/SharedSections";
import { SERVICES, SUCCESS_STORIES, IMAGES, CLINIC } from "../lib/data";

const stats = [
  { icon: ShieldCheck, label: "Evidence-based care" },
  { icon: Users, label: "All ages & abilities" },
  { icon: Activity, label: "Movement-first approach" },
];

export default function Home() {
  const homeServices = [
    ...SERVICES.filter((s) => s.slug === "knee-pain"),
    ...SERVICES.filter((s) => s.slug !== "knee-pain" && s.slug !== "workcover"),
  ];
  return (
    <>
      <Seo
        title="Physiotherapy Sandringham"
        description="Arena Movement Physiotherapy is a physiotherapy clinic in Sandringham offering physiotherapy, sports rehabilitation and exercise-based treatment. Recover from injury, reduce pain and move better. Book online today."
        path="/"
      />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Physiotherapist treating a patient" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <h1 className="sr-only">Physiotherapy in Sandringham</h1>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wide text-white border border-white/20">
              <span className="h-2 w-2 rounded-full bg-emerald-400" /> Now accepting new patients
            </span>
            <div className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.02] text-balance" aria-hidden="true">
              Move Better.<br />Recover Faster.
            </div>
            <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-xl">
              Evidence-based physiotherapy and personalised treatment to help you recover from injury,
              reduce pain and keep doing what you love.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/book"
                data-testid="hero-book-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-navy transition-all hover:-translate-y-0.5 hover:shadow-xl"
              >
                Book Appointment <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/team"
                data-testid="hero-team-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 backdrop-blur px-7 py-4 text-base font-semibold text-white transition-all hover:bg-white/15"
              >
                Meet the Team
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
              {stats.map((s) => (
                <div key={s.label} className="flex items-center gap-2 text-white/85 text-sm">
                  <s.icon className="h-4 w-4 text-emerald-300" /> {s.label}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 sm:py-28" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">About Arena Movement</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight leading-snug">
                Physiotherapy built around how you move
              </h2>
              <p className="mt-5 text-lg text-text-muted leading-relaxed">
                Arena Movement Physiotherapy provides evidence-based physiotherapy, rehabilitation and
                movement-based care. Based within performance and training environments, we work with active
                individuals of all ages, from gym members and recreational athletes to those building strength,
                mobility and confidence in movement.
              </p>
              <ul className="mt-7 space-y-3">
                {["Thorough, hands-on assessment", "Personalised treatment plans", "Progressive exercise rehabilitation", "Clear education every step of the way"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-text-dark">
                    <CheckCircle2 className="h-5 w-5 text-navy shrink-0" /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/about" data-testid="intro-about-link" className="mt-8 inline-flex items-center gap-2 font-semibold text-navy hover:gap-3 transition-all">
                Learn more about us <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] group">
                <img src={IMAGES.athlete} alt="Exercise rehabilitation session" className="w-full h-[460px] object-cover img-zoom" />
              </div>
              <div className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl shadow-xl border border-border-light p-5 flex items-center gap-4">
                <div className="h-12 w-12 grid place-items-center rounded-full bg-navy text-white"><Clock className="h-6 w-6" /></div>
                <div>
                  <div className="font-display text-2xl font-bold text-navy">5.0★</div>
                  <div className="text-sm text-text-muted">Rated on Google</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-28 bg-offwhite" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">Our Services</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight">
                Specialist care for every stage of recovery
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeServices.map((s, i) => {
              const featured = s.slug === "knee-pain";
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className={`group block h-full rounded-3xl p-7 card-hover ${
                      featured
                        ? "bg-navy text-white border border-navy shadow-lg shadow-navy/20"
                        : "bg-white border border-border-light"
                    }`}
                  >
                    {featured && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-white mb-4">
                        <Star className="h-3.5 w-3.5 fill-emerald-300 text-emerald-300" /> Most treated
                      </span>
                    )}
                    <div className={`h-12 w-12 grid place-items-center rounded-2xl transition-colors ${
                      featured ? "bg-white/15 text-white" : "bg-navy/5 text-navy group-hover:bg-navy group-hover:text-white"
                    }`}>
                      <ServiceIcon name={s.icon} />
                    </div>
                    <h3 className={`mt-5 font-display font-semibold ${featured ? "text-2xl text-white" : "text-xl text-text-dark"}`}>{s.title}</h3>
                    <p className={`mt-2 leading-relaxed ${featured ? "text-white/80" : "text-text-muted"}`}>{s.short}</p>
                    <span className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${featured ? "text-white" : "text-navy"}`}>
                      Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="py-20 sm:py-28" data-testid="success-stories">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">Success Stories</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight">
                Real recoveries, real results
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {SUCCESS_STORIES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div className="group h-full bg-white rounded-3xl border border-border-light overflow-hidden card-hover" data-testid={`success-story-${i}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover img-zoom" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-lg font-semibold text-text-dark">{s.summary}</h3>
                    <p className="mt-2 text-sm text-text-muted leading-relaxed">{s.detail}</p>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-red-50 p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-red-500">Before</div>
                        <div className="mt-1 text-sm text-text-dark">{s.before}</div>
                      </div>
                      <div className="rounded-2xl bg-emerald-50 p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">After</div>
                        <div className="mt-1 text-sm text-text-dark">{s.after}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* CTA */}
      <section className="py-20 sm:py-28" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-navy px-8 sm:px-16 py-16 sm:py-20 text-center">
              <h2 className="font-display text-3xl sm:text-5xl font-bold text-white tracking-tight">
                Ready to move better?
              </h2>
              <p className="mt-4 text-lg text-white/75 max-w-xl mx-auto">
                Book your appointment today and take the first step towards a faster, fuller recovery.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/book" data-testid="cta-book-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-navy transition-all hover:-translate-y-0.5">
                  Book Appointment <ArrowRight className="h-4 w-4" />
                </Link>
                <a href={`tel:${CLINIC.phoneIntl}`} data-testid="cta-call-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-4 font-semibold text-white hover:bg-white/10 transition-colors">
                  Call {CLINIC.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
